// features/hive/ui/HiveBodyAnalytics/AnalyticsCard.tsx
import {ReactNode} from 'react';
import {
  AnalyticBody,
  AnalyticBodyCol,
  AnalyticBodyColTitle,
  AnalyticBodyColValueText,
  AnalyticHeader,
  AnalyticRightCol,
} from 'features/hive/ui/HiveBodyAnalytics/styles.ts';
import {ArrowRight} from 'shared/ui/icons/arrowRight.tsx';
import {ChartLegend} from 'widgets/chart/ui/Legend';
import {LegendItem} from 'widgets/chart/model/types';
import {Button} from '@atomaro/ui-kit';
import styled from 'styled-components';
import {COLORS} from 'shared/consts';
import {toRem} from 'shared/helpers';
import {TgIcon} from 'shared/ui/icons';

type AvgItem = {
  name: string;
  value: number | string;
  unit: string;
  status?: 'red' | 'yellow' | 'green';
};

type ExtendedLegendItem = LegendItem & {ts?: number; unit?: 'hour' | 'day' | 'week' | 'month'};

export const ChartPlaceholderWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${toRem(24)};
  padding: ${toRem(40)} ${toRem(20)};
  min-height: ${toRem(260)};
  text-align: center;
`;

export const ChartPlaceholderText = styled.h3`
  color: ${COLORS.FG.DEFAULT};
  font-size: ${toRem(18)};
  font-weight: 600;
  line-height: ${24 / 18};
  margin: 0;
`;

type AnalyticsCardProps = {
  id: 'happiness' | 'temperature' | 'humidity' | 'co2' | 'weight' | 'state';
  icon: ReactNode;
  title: string;
  lastUpdate: string;
  link: string;
  averages: AvgItem[];
  legend?: ExtendedLegendItem[];
  children?: ReactNode;
  hasData?: boolean;
  hasServerData?: boolean;
};

export const AnalyticsCard = ({
  id,
  icon,
  title,
  lastUpdate,
  link,
  averages,
  legend,
  children,
  hasData = true,
  hasServerData = true,
}: AnalyticsCardProps) => {
  return (
    <>
      <AnalyticHeader to={link}>
        {icon}
        <h4>{title}</h4>
        <AnalyticRightCol>
          <p>{lastUpdate}</p>
          {link && <ArrowRight />}
        </AnalyticRightCol>
      </AnalyticHeader>

      {hasData && (
        <AnalyticBody>
          {averages.map((a, i) => (
            <AnalyticBodyCol key={`${id}-avg-${i}`}>
              <AnalyticBodyColTitle>{a.name}</AnalyticBodyColTitle>
              <AnalyticBodyColValueText $status={a.status}>
                <strong>{a.value}</strong> <span>{a.unit}</span>
              </AnalyticBodyColValueText>
            </AnalyticBodyCol>
          ))}
        </AnalyticBody>
      )}
      {hasData ? children : null}

      {!hasData && hasServerData ? (
        <ChartPlaceholderWrapper>
          <ChartPlaceholderText>Нет данных для выбранного диапазона</ChartPlaceholderText>
        </ChartPlaceholderWrapper>
      ) : null}

      {!hasData && !hasServerData ? (
        <ChartPlaceholderWrapper>
          <ChartPlaceholderText>Покупайте наши устройства</ChartPlaceholderText>
          <Button
            label="Написать нам"
            variant="primary"
            size="l"
            iconPrefix={<TgIcon />}
            onClick={() => window.open('https://t.me/Hive_Technical_support_bot')}
          />
        </ChartPlaceholderWrapper>
      ) : null}

      {hasData && legend && legend.length > 0 ? <ChartLegend items={legend} /> : null}
    </>
  );
};
